import type { StrategyChapterKey, StrategyDocument } from "@/types/strategyDocument.type";
import type { DeckGroupId } from "../deck/deckModel";
import { spread } from "../templates/slideText";

/**
 * A slide template of the approved deck (Figma 13 Strategy, Zone plate ·
 * APPROVED; template numbers in the Creo Library) and the part of the
 * document it draws, by index. Templates read the saved document directly.
 */
export type SlideTemplate =
  | { id: "cover" } // 01
  | { id: "opener" } // 02
  | { id: "onepage" } // 38
  | { id: "brief" } // 41
  | { id: "industry-numbers" } // 05
  | { id: "industry-changes"; rows: number[] } // 44
  | { id: "industry-searches"; rows: number[] } // 42
  | { id: "industry-mix" } // 43
  | { id: "industry-conventions"; rows: number[] } // 45
  | { id: "audience" } // 40
  | { id: "persona"; person: number; of: number } // 31
  | { id: "people"; people: number[] } // 06
  | { id: "brand-today" } // 32 (the brand)
  | { id: "community" } // 39
  | { id: "comparison"; competitors: number[] } // 08
  | { id: "snapshot"; competitor: number } // 32 (a competitor)
  | { id: "lessons"; rows: number[] } // 33
  | { id: "map" } // 34
  | { id: "open-space" } // 55
  | { id: "trends"; trends: number[] } // 11
  | { id: "conversation" } // 35
  | { id: "moments" } // 46
  | { id: "moment"; moment: number } // 52
  | { id: "findings" } // 13
  | { id: "method" } // 47
  | { id: "insight" } // 04
  | { id: "idea" } // 48
  | { id: "idea-posts" } // 20
  | { id: "objectives" } // 14
  | { id: "targets" } // 49
  | { id: "platforms"; platforms: number[] } // 15
  | { id: "pillars" } // 16
  | { id: "pillar"; pillar: number } // 17
  | { id: "mix" } // 19
  | { id: "codes" } // 50
  | { id: "year" } // 21
  | { id: "rhythm" } // 51
  | { id: "creators" } // 30
  | { id: "reading" } // 24
  | { id: "tests" } // 54
  | { id: "how" } // 25
  | { id: "sources" } // 26
  | { id: "close" }; // 37

export type SlideTemplateId = SlideTemplate["id"];

export interface StrategySlide {
  id: string;
  /** The key its strategist lines live under in `slide_text`. */
  key: string;
  template: SlideTemplate;
  /** The document chapter it draws (its eyebrow and foot); none for the one page and after the story. */
  chapter?: StrategyChapterKey;
  /** Its group on the plate: 1 Front to 13 Measuring, After, Back. */
  group: DeckGroupId;
  /** The slide's name in the filmstrip, the grid and Creo's context: the plate's label. */
  title: string;
  subtitle: string;
  /** Where Creo scrolls to (Navigate's section): the chapter key. */
  section: string;
  /** For the pillar slides: the document pillar id. */
  pillarId?: string;
  continuation?: number;
}

const indexes = (items: unknown[] | undefined) => (items ?? []).map((_, i) => i);
const NUMBER_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
const countWord = (n: number) => NUMBER_WORDS[n] ?? String(n);

/** The document chapters in the plate's order. */
const CHAPTER_ORDER: readonly StrategyChapterKey[] = [
  "brief", "industry", "audience", "brand", "competitors", "culture", "insight",
  "goals", "platforms", "content", "year", "creators", "measuring",
];
const has = (items: unknown[] | undefined) => !!items && items.length > 0;

/**
 * The deck, slide for slide in the approved plate's order (Figma 13 Strategy,
 * Zone plate · APPROVED, "NN · chapter · label"): 1 Front (the cover, the
 * chapter opener, the one page, the brief), then chapters 2 to 13, each opened
 * by its chapter opener (template 02), then After and Back.
 * Repeating slides: a persona per person, a snapshot per scored competitor, a
 * moment on social per big moment, a slide per pillar. Lists split evenly.
 */
export function buildStrategySlides(doc: StrategyDocument): StrategySlide[] {
  const chapters = new Map<StrategyChapterKey, StrategySlide[]>();
  const push = (chapter: StrategyChapterKey, slide: Omit<StrategySlide, "chapter" | "group" | "section">) => {
    const list = chapters.get(chapter) ?? [];
    list.push({ ...slide, chapter, group: chapter === "brief" ? "front" : (chapter as DeckGroupId), section: chapter });
    chapters.set(chapter, list);
  };

  /* 1 The brief. */
  const brief = doc.brief;
  if (brief && (brief.recap || brief.business_objective || brief.social_objective)) {
    push("brief", { id: "brief", key: "brief", template: { id: "brief" }, title: "The brief", subtitle: "The client’s words, two objectives" });
  }

  /* 2 The industry. */
  const industry = doc.industry;
  if (industry) {
    if (industry.summary || has(industry.numbers)) push("industry", { id: "industry-numbers", key: "industry.numbers", template: { id: "industry-numbers" }, title: "In numbers", subtitle: "Numbers, growing and shrinking" });
    spread(indexes(industry.changes), 5).forEach((rows, n) => push("industry", { id: `industry-changes:${n}`, key: "industry.changes", template: { id: "industry-changes", rows }, title: "What is changing", subtitle: "Habits, players, platforms, rules", continuation: n }));
    const featured = industry.top_searches.map((s, i) => ({ s, i })).filter(({ s }) => s.featured !== false).map(({ i }) => i);
    spread(featured, 5).forEach((rows, n) => push("industry", { id: `industry-searches:${n}`, key: "industry.searches", template: { id: "industry-searches", rows }, title: "What people search for", subtitle: "Ranks and hashtag counts", continuation: n }));
    const mixes = (doc.brand?.content_mix ? 1 : 0) + (doc.competitors?.set ?? []).filter((c) => c.content_mix).length;
    if (mixes > 0) push("industry", { id: "industry-mix", key: "industry.mix", template: { id: "industry-mix" }, title: "What they post", subtitle: "One class per post" });
    spread(indexes(industry.conventions), 5).forEach((rows, n) => push("industry", { id: `industry-conventions:${n}`, key: "industry.conventions", template: { id: "industry-conventions", rows }, title: "What everyone does", subtitle: "The category’s habits", continuation: n }));
  }

  /* 3 The audience. */
  const audience = doc.audience;
  if (audience) {
    if (has(audience.who) || has(audience.questions) || has(audience.behaviour?.platforms_by_country) || has(audience.behaviour?.online_times)) {
      push("audience", { id: "audience", key: "audience.overview", template: { id: "audience" }, title: "The audience", subtitle: "Where, who, when, what they ask" });
    }
    audience.people.forEach((person, i) => push("audience", { id: `persona:${person.id}`, key: `audience.person:${person.id}`, template: { id: "persona", person: i, of: audience.people.length }, title: person.name, subtitle: `Persona ${i + 1} of ${audience.people.length}` }));
    spread(indexes(audience.people), 3).forEach((people, n) => push("audience", { id: `people:${n}`, key: "audience.people", template: { id: "people", people }, title: `${countWord(audience.people.length)} people, one table`, subtitle: "Recap", continuation: n }));
  }

  /* 4 The brand today. */
  const brand = doc.brand;
  if (brand && (has(brand.accounts) || brand.scorecard)) {
    push("brand", { id: "brand-today", key: "brand.today", template: { id: "brand-today" }, title: "The brand today", subtitle: "Presence, content, the score" });
  }
  if (brand?.collaborations || brand?.scorecard?.criteria.some((c) => c.criterion_id === "M1" || c.criterion_id === "M3")) {
    push("brand", { id: "community", key: "brand.community", template: { id: "community" }, title: "Community", subtitle: "How it talks to people, who it works with" });
  }

  /* 5 The competitors. */
  const competitors = doc.competitors;
  const scored = (competitors?.set ?? []).map((c, i) => ({ c, i })).filter(({ c }) => c.status !== "dormant");
  if (competitors && scored.length) {
    spread(scored.map(({ i }) => i), 4).forEach((list, n) => push("competitors", { id: `comparison:${n}`, key: "competitors.comparison", template: { id: "comparison", competitors: list }, title: "Comparison", subtitle: "The set, scored", continuation: n }));
    scored.forEach(({ c, i }) => push("competitors", { id: `snapshot:${c.id}`, key: `competitors.snapshot:${c.id}`, template: { id: "snapshot", competitor: i }, title: c.name, subtitle: "Competitor snapshot" }));
  }
  if (competitors && has(competitors.lessons)) {
    spread(indexes(competitors.lessons), 4).forEach((rows, n) => push("competitors", { id: `lessons:${n}`, key: "competitors.lessons", template: { id: "lessons", rows }, title: "What we learn from each", subtitle: "Well, room, our angle", continuation: n }));
  }
  if (competitors?.whitespace_map) push("competitors", { id: "map", key: "competitors.map", template: { id: "map" }, title: "The whitespace map", subtitle: "Who sits where" });
  if (competitors && has(competitors.territories)) push("competitors", { id: "open-space", key: "competitors.open_space", template: { id: "open-space" }, title: "Open space by the numbers", subtitle: "Share of posts by topic" });

  /* 6 Culture and conversation. */
  const culture = doc.culture;
  if (culture) {
    spread(indexes(culture.trends), 4).forEach((trends, n) => push("culture", { id: `trends:${n}`, key: "culture.trends", template: { id: "trends", trends }, title: "Trends", subtitle: "Stage and dates as observed", continuation: n }));
    if (culture.conversation && (has(culture.conversation.themes) || culture.conversation.mood)) push("culture", { id: "conversation", key: "culture.conversation", template: { id: "conversation" }, title: "What people say", subtitle: "Themes counted, quotes, mood" });
    if (has(culture.cultural_moments)) push("culture", { id: "moments", key: "culture.moments", template: { id: "moments" }, title: "The moments", subtitle: "Market by market" });
    culture.cultural_moments.forEach((m, i) => {
      if (m.is_big && m.on_social) push("culture", { id: `moment:${m.id}`, key: `culture.moment:${m.id}`, template: { id: "moment", moment: i }, title: `${m.moment} on social`, subtitle: "A moment on social" });
    });
  }

  /* 7 The insight and the idea. */
  const insight = doc.insight;
  if (insight) {
    if (has(insight.findings)) push("insight", { id: "findings", key: "insight.findings", template: { id: "findings" }, title: "What the research says", subtitle: "One finding per chapter" });
    if (insight.method && (insight.method.convention || insight.method.belief)) push("insight", { id: "method", key: "insight.method", template: { id: "method" }, title: "The method", subtitle: "The logo-swap test" });
    if (has(insight.insights)) push("insight", { id: "insight", key: "insight.insight", template: { id: "insight" }, title: "The insight", subtitle: "With its proof" });
    if (insight.idea?.line) push("insight", { id: "idea", key: "insight.idea", template: { id: "idea" }, title: "The idea", subtitle: "In one line" });
    if (has(insight.idea?.example_posts)) push("insight", { id: "idea-posts", key: "insight.posts", template: { id: "idea-posts" }, title: "The idea in three posts", subtitle: "Example posts" });
  }

  /* 8 Goals and targets. */
  if (brief?.business_objective || has(doc.goals?.list)) push("goals", { id: "objectives", key: "goals.objectives", template: { id: "objectives" }, title: "Objectives", subtitle: "The ambition" });
  if (has(doc.goals?.list)) push("goals", { id: "targets", key: "goals.targets", template: { id: "targets" }, title: "Targets", subtitle: "Three numbers, one year" });

  /* 9 Platforms. */
  spread(indexes(doc.platforms?.list), 4).forEach((platforms, n) => push("platforms", { id: `platforms:${n}`, key: "platforms.cards", template: { id: "platforms", platforms }, title: "Platforms", subtitle: "One job each", continuation: n }));

  /* 10 The content. */
  const content = doc.content;
  if (content) {
    if (has(content.pillars)) push("content", { id: "pillars", key: "content.pillars", template: { id: "pillars" }, title: "Pillars", subtitle: "With their class" });
    content.pillars.forEach((p, i) => push("content", { id: `pillar:${p.id}`, key: `content.pillar:${p.id}`, template: { id: "pillar", pillar: i }, title: p.name, subtitle: `Pillar ${i + 1} of ${content.pillars.length}`, pillarId: p.id }));
    if (content.volume) push("content", { id: "mix", key: "content.mix", template: { id: "mix" }, title: "The mix and the volume", subtitle: "Posts a month" });
    if (has(content.brand_codes)) push("content", { id: "codes", key: "content.codes", template: { id: "codes" }, title: "Brand codes", subtitle: "Ours in the first two seconds" });
  }

  /* 11 The year. */
  const year = doc.year;
  if (year && (has(year.year_moments) || has(year.big_moments) || has(year.quarters))) push("year", { id: "year", key: "year.calendar", template: { id: "year" }, title: "The year", subtitle: "Month by month" });
  if (content?.volume && (year?.reaction || has(content.volume.per_platform))) push("year", { id: "rhythm", key: "year.rhythm", template: { id: "rhythm" }, title: "The rhythm", subtitle: "Posts a week, the hours" });

  /* 12 Creators. */
  if (has(doc.creators?.list) || has(doc.creators?.own_people)) push("creators", { id: "creators", key: "creators.list", template: { id: "creators" }, title: "Our creators", subtitle: "One job each" });

  /* 13 Measuring and learning. */
  if (doc.measuring || has(doc.goals?.list)) push("measuring", { id: "reading", key: "measuring.reading", template: { id: "reading" }, title: "Five reads", subtitle: "Five reads, three rules" });
  if (has(doc.measuring?.tests)) push("measuring", { id: "tests", key: "measuring.tests", template: { id: "tests" }, title: "Tests", subtitle: "Read after six posts each" });

  /* The deck. 1 Front: the cover, the chapter opener, the one page, the brief. */
  const opener = (key: StrategyChapterKey, group: DeckGroupId): StrategySlide => ({
    id: `opener:${key}`, key: `opener:${key}`, template: { id: "opener" }, chapter: key, group, title: "Chapter opener", subtitle: "Chapter opener", section: key,
  });
  const deck: StrategySlide[] = [
    { id: "cover", key: "cover", template: { id: "cover" }, chapter: "brief", group: "front", title: "Cover", subtitle: "The cover", section: "brief" },
  ];
  const hasStory = [...chapters.values()].some((list) => list.length);
  if (hasStory) {
    deck.push(opener("brief", "front"));
    deck.push({ id: "onepage", key: "onepage", template: { id: "onepage" }, group: "front", title: "The strategy on one page", subtitle: "What we commit to", section: "summary" });
  }
  /* 2 to 13, in the plate's order (brief first: it closes Front), each opened by its chapter opener. */
  for (const key of CHAPTER_ORDER) {
    const list = chapters.get(key) ?? [];
    if (list.length && key !== "brief") deck.push(opener(key, list[0].group));
    deck.push(...list);
  }
  /* After, and Back. */
  const record = doc.record;
  if (record?.how_we_did_this) deck.push({ id: "how", key: "record.how", template: { id: "how" }, group: "after", title: "How we did this", subtitle: "Counted, not claimed", section: "record" });
  if (record && (has(record.sources) || has(record.approvals) || has(record.open_items))) deck.push({ id: "sources", key: "record.sources", template: { id: "sources" }, group: "after", title: "Sources and the record", subtitle: "Every source, every change", section: "record" });
  if (insight?.idea?.line) deck.push({ id: "close", key: "close", template: { id: "close" }, group: "back", title: "Close", subtitle: "What this strategy asks", section: "insight" });
  return deck;
}
